/**
 * OptionsDropdown Component
 *
 * A reusable dropdown for detail pages. Owns the open/close controller —
 * outside-click, Escape, viewport clamping — for both of its shapes:
 *
 *   1. ACTION MENU (default): builds its own kebab trigger and renders an
 *      `items` list. Mounted by RightPanelManager and section detail views.
 *   2. DISCLOSURE PANEL: adopts an existing `trigger` and renders arbitrary
 *      `body` markup. Used by PathProgressPopover for a read-only data panel.
 *
 * The second shape exists so panels don't clone this controller — see the
 * consolidation note at the top of optionsMenu.js.
 *
 * @module components/OptionsDropdown
 *
 * @example <caption>Action menu</caption>
 * const dropdown = new OptionsDropdown({
 *   container: element,
 *   items: [
 *     { action: 'copy', label: 'Copy' },
 *     { action: 'delete', label: 'Delete', variant: 'danger' }
 *   ],
 *   onAction: async (action) => {
 *     if (action === 'copy') { await copyToClipboard(); }
 *     else if (action === 'delete') { await handleDelete(); }
 *   }
 * });
 *
 * @example <caption>Disclosure panel on an existing trigger</caption>
 * const panel = new OptionsDropdown({
 *   trigger: document.getElementById('header-remaining'),
 *   body: () => '<div>…</div>',
 *   menuClass: 'path-progress-popover',
 *   menuId: 'path-progress-panel'
 * });
 *
 * // Clean up when done
 * dropdown.destroy();
 */
import { KEBAB_GLYPH, renderMenuItems, keepMenuInViewport } from './optionsMenu.js';

/**
 * The one dropdown open anywhere on the page, or null. A detail page's header
 * carries two of these side by side (Contents + the Options kebab), and each
 * trigger stops its click from propagating, so the other's outside-click
 * listener never sees it. Opening one therefore closes whichever is open.
 * @type {OptionsDropdown|null}
 */
let openDropdown = null;

export class OptionsDropdown {
  /**
   * @param {Object} config - Component configuration
   * @param {HTMLElement} [config.container] - Container to append the dropdown to.
   *   Required unless `trigger` is given.
   * @param {Array<Object>|Function} [config.items] - Menu items array or a function
   *   returning the current items each time the menu opens. Ignored when `body` is given.
   * @param {string} config.items[].action - Action identifier
   * @param {string} config.items[].label - Display label
   * @param {string} [config.items[].variant] - Optional variant ('danger' for destructive actions)
   * @param {Function} [config.onAction] - Callback when action is selected (receives action string)
   * @param {string} [config.buttonText='Options'] - Accessible label for the icon-only trigger button
   * @param {HTMLElement} [config.trigger] - Adopt this element as the trigger instead
   *   of building a kebab button. Its PARENT becomes the positioning wrapper, so that
   *   parent must contain the trigger and nothing that should sit outside the panel's
   *   anchor. The wrapper is page-owned: destroy() leaves it in the DOM.
   * @param {Function} [config.body] - Returns panel markup, called on each open.
   *   Replaces the `items` list; no item listeners are bound and the panel gets no
   *   `role="menu"` (it is a disclosure, not a menu).
   * @param {string} [config.menuClass] - Extra class(es) on the panel element.
   * @param {string} [config.menuId] - Panel id, wired to the trigger's `aria-controls`.
   * @param {Function} [config.canOpen] - Predicate consulted before opening. Returning
   *   false aborts the open entirely — nothing renders and no aria state changes.
   * @param {Function} [config.onOpen] - Called once the panel is visible. The place to
   *   start an async load, since `body` itself must return synchronously.
   * @param {Function} [config.onClose] - Called after the panel closes.
   */
  constructor(config) {
    this.container = config.container;
    this.items = config.items;
    this.onAction = config.onAction;
    this.buttonText = config.buttonText || 'Options';
    this.trigger = config.trigger || null;
    this.body = config.body || null;
    this.menuClass = config.menuClass || '';
    this.menuId = config.menuId || '';
    this.canOpen = config.canOpen || null;
    this.onOpen = config.onOpen || null;
    this.onClose = config.onClose || null;

    // State
    this.isOpen = false;
    // True when this component built the wrapper and may therefore remove it.
    this._ownsWrapper = false;

    // Element references
    this.wrapperElement = null;
    this.buttonElement = null;
    this.menuElement = null;
    this.outsideClickTimer = null;

    // Bound methods for event cleanup
    this.boundToggle = this.handleToggle.bind(this);
    this.boundOutsideClick = this.handleOutsideClick.bind(this);
    this.boundKeydown = this.handleKeydown.bind(this);
    this.boundItemClick = this.handleItemClick.bind(this);

    this.initialize();
  }

  /**
   * Initialize component
   */
  initialize() {
    this.render();
    this.attachEventListeners();
  }

  /**
   * Render the dropdown
   */
  render() {
    let wrapper;
    let button;

    if (this.trigger) {
      // Adopted trigger: the page owns both it and the wrapper it sits in.
      button = this.trigger;
      wrapper = this.trigger.parentElement;
      this._ownsWrapper = false;
    } else {
      wrapper = document.createElement('div');
      wrapper.className = 'options-dropdown';
      this._ownsWrapper = true;

      // Options button — icon-only kebab (dot-dot-dot)
      button = document.createElement('button');
      button.className = 'options-dropdown__button';
      button.type = 'button';
      // Only the action-menu shape is a menu. A `body` panel is a disclosure:
      // it holds no focusable content, so promising a menu (or a dialog) would
      // advertise a focus move that never happens.
      button.setAttribute('aria-haspopup', 'menu');
      button.setAttribute('aria-label', this.buttonText);
      button.innerHTML = KEBAB_GLYPH;
    }
    button.setAttribute('aria-expanded', 'false');

    // Dropdown menu — items (with leading icons) built by the shared renderer
    // so this component and the row kebabs stay byte-identical.
    const menu = document.createElement('div');
    menu.className = `options-dropdown__menu options-dropdown__menu--hidden${this.menuClass ? ` ${this.menuClass}` : ''}`;
    if (this.menuId) {
      menu.id = this.menuId;
      button.setAttribute('aria-controls', this.menuId);
    }
    if (this.body) {
      menu.innerHTML = this.body();
    } else {
      menu.setAttribute('role', 'menu');
      menu.innerHTML = renderMenuItems(this._resolveItems());
    }

    if (this._ownsWrapper) wrapper.appendChild(button);
    wrapper.appendChild(menu);

    // Store references
    this.wrapperElement = wrapper;
    this.buttonElement = button;
    this.menuElement = menu;

    // Append to container — an adopted trigger is already in the DOM.
    if (this._ownsWrapper) this.container.appendChild(wrapper);
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    this.buttonElement.addEventListener('click', this.boundToggle);

    if (!this.body) this._attachItemListeners();
  }

  /** @returns {Array<Object>} The current menu items. */
  _resolveItems() {
    return typeof this.items === 'function' ? this.items() : this.items;
  }

  /** Attach the shared click handler to the menu's current item nodes. */
  _attachItemListeners() {
    this.menuElement.querySelectorAll('.options-dropdown__item').forEach(item => {
      item.addEventListener('click', this.boundItemClick);
    });
  }

  /** Re-render dynamic content without retaining references to replaced nodes. */
  _renderDynamicItems() {
    if (this.body) {
      this.menuElement.innerHTML = this.body();
      return;
    }
    if (typeof this.items !== 'function') return;
    this.menuElement.innerHTML = renderMenuItems(this._resolveItems());
    this._attachItemListeners();
  }

  /**
   * Handle button toggle
   * @param {Event} event
   */
  handleToggle(event) {
    event.stopPropagation();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the dropdown menu
   */
  open() {
    if (this.isOpen) return;
    if (this.canOpen && !this.canOpen()) return;

    this._renderDynamicItems();

    if (openDropdown && openDropdown !== this) openDropdown.close();
    openDropdown = this;

    this.isOpen = true;
    this.buttonElement.setAttribute('aria-expanded', 'true');
    this.menuElement.classList.remove('options-dropdown__menu--hidden');
    this.wrapperElement.classList.add('options-dropdown--open');
    // After the menu is visible — it measures as 0×0 while hidden. The detail
    // panel's header packs several buttons to the right of this trigger, so a
    // right-anchored menu can overrun the left edge of the screen on mobile.
    keepMenuInViewport(this.menuElement);
    document.addEventListener('keydown', this.boundKeydown);

    // Add outside click listener after a brief delay to prevent immediate close
    this.outsideClickTimer = setTimeout(() => {
      this.outsideClickTimer = null;
      if (this.isOpen) document.addEventListener('click', this.boundOutsideClick);
    }, 0);

    this.onOpen?.();
  }

  /**
   * Re-run `body` into the open panel. For async panels whose content arrives
   * after open() — `body` must be synchronous, so the load lands here.
   */
  refreshBody() {
    if (!this.body || !this.menuElement) return;
    this.menuElement.innerHTML = this.body();
  }

  /**
   * Close the dropdown menu
   */
  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    if (openDropdown === this) openDropdown = null;
    this.buttonElement.setAttribute('aria-expanded', 'false');
    this.menuElement.classList.add('options-dropdown__menu--hidden');
    this.wrapperElement.classList.remove('options-dropdown--open');

    document.removeEventListener('click', this.boundOutsideClick);
    document.removeEventListener('keydown', this.boundKeydown);
    if (this.outsideClickTimer !== null) {
      clearTimeout(this.outsideClickTimer);
      this.outsideClickTimer = null;
    }

    this.onClose?.();
  }

  /**
   * Close an open menu with Escape and return focus to its trigger.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.preventDefault();
    this.close();
    this.buttonElement?.focus();
  }

  /**
   * Handle clicks outside the component
   * @param {Event} event
   */
  handleOutsideClick(event) {
    if (this.wrapperElement && !this.wrapperElement.contains(event.target)) {
      this.close();
    }
  }

  /**
   * Handle menu item click
   * @param {Event} event
   */
  handleItemClick(event) {
    event.stopPropagation();

    const action = event.currentTarget.getAttribute('data-action');

    // Close menu first
    this.close();

    // Then trigger action callback. onAction isn't awaited here (callers may
    // return void or a Promise), so guard against a throw or an unhandled
    // rejection silently swallowing an action failure.
    if (action && this.onAction) {
      try {
        const result = this.onAction(action);
        if (result && typeof result.catch === 'function') {
          result.catch((error) => console.error('[OptionsDropdown] Action failed:', error));
        }
      } catch (error) {
        console.error('[OptionsDropdown] Action failed:', error);
      }
    }
  }

  /**
   * Get the wrapper element (for appending to DOM manually if needed)
   * @returns {HTMLElement}
   */
  getElement() {
    return this.wrapperElement;
  }

  /**
   * Clean up event listeners and DOM
   */
  destroy() {
    // Close if open
    if (this.isOpen) {
      this.close();
    }

    // Remove event listeners
    if (this.buttonElement) {
      this.buttonElement.removeEventListener('click', this.boundToggle);
    }

    document.removeEventListener('click', this.boundOutsideClick);
    document.removeEventListener('keydown', this.boundKeydown);
    if (this.outsideClickTimer !== null) {
      clearTimeout(this.outsideClickTimer);
      this.outsideClickTimer = null;
    }

    // Remove from DOM. An adopted trigger's wrapper belongs to the page, so only
    // the panel this component built comes out; leave the aria state clean.
    if (this._ownsWrapper) {
      this.wrapperElement?.parentNode?.removeChild(this.wrapperElement);
    } else {
      this.menuElement?.parentNode?.removeChild(this.menuElement);
      this.buttonElement?.removeAttribute('aria-expanded');
      this.buttonElement?.removeAttribute('aria-controls');
    }

    // Clear references
    this.wrapperElement = null;
    this.buttonElement = null;
    this.menuElement = null;
  }
}
